import { ScrEnvironment } from './../../environments/environment';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ScrBaseResponse, IScrBaseResponse, ScrBaseService } from './base.service';
import { ScrServiceManager } from './servicemanager.service';
import { WITHCRED, WithCredNoCache } from './authentication.service';
import { Account, AccountSummary, AdminInfoResponse, LinkedAccountListResponse } from './../models/account.model';
import { SystemMessagesResponse } from '../models/system-messages.model';
import { CacheService } from './cache.service';
import { NGXLogger } from 'ngx-logger';



export class LinkedAccountCreateRequestAccountInfo {

  fein?: string;
  companyUrl?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  phoneExtension?: string;
  email?: string;
  billingFirstName?: string;
  billingLastName?: string;
  billingAddress1?: string;
  billingAddress2?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  billingCountry?: string;
  billingPhone?: string;
  billingPhoneExtension?: string;
  billingEmail?: string;
  linkedAccountName?: string;
}

export class LinkedAccountCreateRequestUserInfo {

  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  phoneExtension?: string;
  email?: string;

  // client-side extensions.
  confirmEmail?: string;
}

export class LinkedAccountCreateRequest {

  constructor() {
    this.account = new LinkedAccountCreateRequestAccountInfo();
    this.user = new LinkedAccountCreateRequestUserInfo();
  }
  account!: LinkedAccountCreateRequestAccountInfo;
  user!: LinkedAccountCreateRequestUserInfo;
}



export class CreateAccountRequest extends Account {
  username?: string;
  password?: string;
}

export interface GetAccountResponse extends IScrBaseResponse {
  account: Account;
}

export class GetAccountSummaryResponse extends ScrBaseResponse {
  accountSummary: AccountSummary = new AccountSummary();
}

export class MonthlyBillingOptOutResponse extends ScrBaseResponse {

  // these are returned when preparing for deactivation of MB.
  public codeCount?: number;
  public paymentStatus?: string; // Paid, PaymentDue, PaymentOverdue, Cancelled.
}

@Injectable({
  providedIn: 'root'
})
export class AccountService extends ScrBaseService {

  constructor(
    serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,
    private cache: CacheService,
    private logger: NGXLogger,
    private http: HttpClient) {
    super(serviceManager);

  }

  public getAccount(clearcache = false, pullcredit = false): Observable<GetAccountResponse> {
    if (clearcache) {
      this.cache.invalidate('account', false);
    }

    const acct = this.cache.get<Account>('account');
    if (acct) {
      const ar: GetAccountResponse = {
        account: acct,
        status: 'success',
        messages: []
      };
      this.logger.debug('use cached account');
      return of(ar);
    }

    let url = `${this.environment.base}/secure/account`;
    if (pullcredit) {
      url = url + `?pullcredit=${pullcredit}`;
    }
    return this.http.get<GetAccountResponse>(url, WithCredNoCache).pipe(tap(rsp => {
      if ((rsp.status === 'success') && (rsp.account)) {
        this.cache.set('account', rsp.account, Number.MAX_SAFE_INTEGER);
      }
    }));

  }

  public getAccountWithSummary(pullcredit = false): Observable<GetAccountSummaryResponse> {
    let url = `${this.environment.base}/secure/account/summary`;
    if (pullcredit) {
      url = url + `?pullcredit=${pullcredit}`;
    }
    return this.http.get<GetAccountSummaryResponse>(url, WithCredNoCache);
  }

  public verifyAccount(nonce: string): Observable<object> {
    const url = `${this.environment.base}/public/sec/verifyemail/${nonce}`;
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('x-api-key', this.environment.api.scr.apikey);
    return this.http.get<object>(url, { headers });
  }

  public resendVerification(nonce: string): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/public/sec/resendemail/${nonce}`;
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('x-api-key', this.environment.api.scr.apikey);
    return this.http.get<ScrBaseResponse>(url, { headers });
  }

  public fetchMessages(): Observable<SystemMessagesResponse> {
    const url = `${this.environment.base}/secure/messages`;
    return this.http.get<SystemMessagesResponse>(url, WithCredNoCache);
  }

  public readMessage(messageId: number): Observable<SystemMessagesResponse> {
    const url = `${this.environment.base}/secure/messages`;
    const payload = { messageIds: [messageId], action: 'read' };
    return this.http.put<SystemMessagesResponse>(url, payload, WITHCRED);
  }

  public deleteMessages(messageIds: (number | undefined)[]): Observable<SystemMessagesResponse> {
    const url = `${this.environment.base}/secure/messages`;
    const payload = { messageIds: messageIds.filter(mid => mid !== undefined), action: 'delete' };
    return this.http.put<SystemMessagesResponse>(url, payload, WITHCRED);
  }

  public getAccountAdmins(): Observable<AdminInfoResponse> {
    const url = `${this.environment.base}/secure/account/admin-list`;
    return this.http.get<AdminInfoResponse>(url, WithCredNoCache);
  }

  public getLinkedAccounts(): Observable<LinkedAccountListResponse> {
    const url = `${this.environment.base}/secure2/account/linked`;
    return this.http.get<LinkedAccountListResponse>(url, WithCredNoCache);
  }

  public createLinkedAccount(req: LinkedAccountCreateRequest): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/secure2/account/linkedaccounts`;
    const strBody = JSON.stringify(req, ScrBaseService.phoneReplacer);
    return this.http.post<IScrBaseResponse>(url, strBody, WithCredNoCache);
  }
}